import { Inter } from '@/assets/fonts/font';
import BetterCotton from '@/assets/images/better_cotton.png';
import CottonWithStickiness from '@/assets/images/cotton_with_stickiness.png';
import OrganicCotton from '@/assets/images/organic_cotton.png';
import { ImprovedCottonEnum } from '@/common/constants/improved.cotton.constants';
import { formatDate } from '@/lib/format-date';
import i18next from 'i18next';
import jsPDF from 'jspdf';
import { ILoad } from '../../models';

const t = i18next.t;

/**
 * Improved cotton icon HTML generator
 */
const getCottonIconHtml = (
  icon_type:
    | ImprovedCottonEnum.BETTER_COTTON
    | ImprovedCottonEnum.COTTON_WITH_STICKINESS
    | ImprovedCottonEnum.ORGANIC
    | null,
) => {
  if (!icon_type) return '';

  switch (icon_type) {
    case ImprovedCottonEnum.BETTER_COTTON:
      return `<img src="${BetterCotton}" alt="better-cotton" style="width:150px;"/>`;

    case ImprovedCottonEnum.COTTON_WITH_STICKINESS:
      return `<img src="${CottonWithStickiness}" alt="cotton-with-stickiness" style="width:150px;" />`;

    case ImprovedCottonEnum.ORGANIC:
      return `<img src="${OrganicCotton}" alt="organic-cotton" style="width:150px;" />`;

    default:
      return '';
  }
};

/**
 * Single document block generator
 */
const getDocBlock = (
  contract: any,
  org: any,
  id: string,
  improved_cotton: ImprovedCottonEnum | null,
  created_at: string,
  load: ILoad,
) => {
  return `
    <div style="display: flex; flex-direction: column; align-items: center; padding: 0.5rem 1rem; gap: 0.8rem; margin-bottom: 5px; box-sizing: border-box;">
      <div style="width: 100%; display: flex; flex-direction: column; font-size: 0.85rem;">
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
          <div style="display: flex; flex-direction: column; gap: 2px;">
            <p style="margin: 0;">
              ${t('cotton.cleaning.enterprise')}: ${contract?.buyer_name}
            </p>
            <p style="margin: 0;">
              ${t('cotton.preparation.point')}: ${org?.place_name}
            </p>
            <p style="margin: 0;">
              ${t('farmer.name')}: ${load?.farmer_name}
            </p>
            <p style="margin: 0;">
              ${t('farmer.inn')}: ${load?.tax_number}
            </p>
          </div>

          <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 2px;">
            <p style="font-size: 1.125rem; font-weight: 600; margin: 0;">2XL</p>
            <p style="font-size: 1.25rem; text-align: right; margin: 0;">
              ${t('id.number.colon')}
              <span style="font-size: 1.5rem; font-weight: 700;">${id}</span>
            </p>
            <p style="font-size: 13px; margin: 0;">
              ${t('created.at')}: ${formatDate(created_at)}
            </p>
          </div>
        </div>
      </div>

      ${
        improved_cotton
          ? `<div style="margin: 8px 0; display: flex; justify-content: center; align-items: center;">
              ${getCottonIconHtml(improved_cotton)}
             </div>`
          : ''
      }

      <div style="display: flex; flex-direction: column; align-items: center; gap: 2px;">
        <p style="font-weight: 500; margin: 0;">${t('cotton.analysis.results')}</p>
        <h3 style="font-size: 1.1rem; font-weight: 600; margin: 0;">${t('information.note.22')}</h3>
      </div>

      <div style="width: 100%; display: flex; justify-content: space-between; align-items: center;">
        <p style="font-size: 1rem; font-weight: 600; margin: 0;">${t('laboratory.conclusion')}</p>
        <p style="font-size: 0.85rem; font-weight: 500; margin: 0;">${org.district_name || '---'}</p>
      </div>

      <div style="width: 100%; display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; font-size: 0.85rem;">
        <div>
          <div style="display: flex; align-items: flex-end; width: 95%; margin-bottom: 0.6rem;">
            <span style="font-weight: 500; white-space: nowrap;">${t('cotton.pollution')}</span>
            <div style="flex-grow: 1; border-bottom: 2px solid #5E5E5E; margin: 0 0.5rem;"></div>
            <span>%</span>
          </div>
          <div style="display: flex; align-items: flex-end; width: 95%; margin-bottom: 1.2rem;">
            <span style="font-weight: 500; white-space: nowrap;">${t('cotton.humidity')}</span>
            <div style="flex-grow: 1; border-bottom: 2px solid #5E5E5E; margin: 0 0.5rem;"></div>
            <span>%</span>
          </div>
          <div style="display: flex; align-items: flex-end;">
            <span style="font-weight: 500; white-space: nowrap;">${t('laboratory.manager')}</span>
            <div style="flex-grow: 1; border-bottom: 2px solid #5E5E5E; margin-left: 0.5rem;"></div>
          </div>
        </div>

        <div>
          <div style="display: flex; align-items: flex-end; margin-bottom: 0.6rem;">
            <span style="font-weight: 500; margin-right: 0.3rem; white-space: nowrap;">${t('cotton.variety')}</span>
            <div style="flex-grow: 1; border-bottom: 2px solid #5E5E5E; margin: 0 0.5rem;"></div>
            <span style="font-weight: 500; margin-right: 0.3rem; white-space: nowrap;">${t('cotton.class')}</span>
            <div style="flex-grow: 1; border-bottom: 2px solid #5E5E5E; margin-left: 0.3rem;"></div>
          </div>
          <div style="display: flex; align-items: flex-end; margin-bottom: 1.2rem;">
            <span style="font-weight: 500; white-space: nowrap;">${t('senior.laboratory.assistant')}</span>
            <div style="flex-grow: 1; border-bottom: 2px solid #5E5E5E; margin-left: 0.5rem;"></div>
          </div>
          <div style="display: flex; align-items: flex-end;">
            <span style="font-weight: 500; white-space: nowrap;">${t('farm.representative')}</span> 
            <div style="flex-grow: 1; border-bottom: 2px solid #5E5E5E; margin-left: 0.5rem;"></div>
          </div>
        </div>
      </div>
    </div>
  `;
};

/**
 * PDF generator
 */
export const generatePDF = (
  contract: any,
  org: any,
  id: string,
  improved_cotton: ImprovedCottonEnum | null,
  created_at: string,
  load: ILoad,
) => {
  return new Promise<void>((resolve, reject) => {
    try {
      const repeatCount = improved_cotton ? 2 : 3;

      const blocks = Array.from({ length: repeatCount })
        .map(() =>
          getDocBlock(contract, org, id, improved_cotton, created_at, load),
        )
        .join('');

      const template = `
        <!DOCTYPE html>
        <html lang="ru">
        <head>
          <meta charset="UTF-8">
          <title>PDF Content</title>
          <style>
            * { font-family: 'Inter', sans-serif; }
          </style>
        </head>
        <body>
          <div class="container" style="color: #121212">
            ${blocks}
          </div>
        </body>
        </html>
      `;

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'px',
        format: [794, 1123], // A4
      });

      // TTF shriftni qo‘shish
      pdf.addFileToVFS('Inter.ttf', Inter);
      pdf.addFont('Inter.ttf', 'Inter', 'normal');
      pdf.setFont('Inter');

      pdf.html(template, {
        callback: (doc) => {
          doc.save(`malumotnoma-${id}.pdf`);
          resolve();
        },
        x: 10,
        y: 10,
        width: 780,
        windowWidth: 900,
      });
    } catch (error) {
      reject(error);
    }
  });
};
